'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, type AnimationEvent } from 'react';
import { BookOpen, Rotate3d, Package } from 'lucide-react';
import type { ModelViewer } from '@/lib/bricks/viewer/model-viewer';
import type { BoxViewer } from '@/lib/bricks/viewer/box-viewer';
import type { BookletRuntime } from '@/lib/bricks/booklet-runtime';
export type Stage = 'book' | 'model' | 'box';
// How a stage change touches the address bar: 'push' opens a new history
// entry from the booklet, 'replace' swaps sibling views (model <-> box)
// without adding one, 'none' is a popstate/mount restore that shouldn't
// touch history at all — it's already where the address says.
type Nav = 'push' | 'replace' | 'none';

// How long a stage lingers under its replacement: bricks-viewer-out's
// 260ms delay + 520ms fade in viewer.css, which also covers the 420ms
// bricks-viewer-in of the stage coming in.
const HANDOFF_MS = 800;
const urlFor = (stage: 'model' | 'box') => `${window.location.pathname}?view=${stage === 'model' ? '3d' : 'box'}`;

// The open booklet's other two stages: its finished build in 3D (?view=3d)
// and its box (?view=box), swapped in over the booklet like a segmented
// control. `mounted` per stage so a reading session only ever builds one
// WebGLRenderer of each kind (BuildViewer/BoxStage's `hidden` prop keeps the
// unseen one suspended rather than tearing it down); `viewing` covers the
// model's async leave() animation lingering after the booklet is back, and
// `boxShown` the box lingering under whatever replaces it.
export function useStageView() {
  const $ = _c(20);
  const [stage, setStage] = useState("book");
  const [modelMounted, setModelMounted] = useState(false);
  const [boxMounted, setBoxMounted] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [boxShown, setBoxShown] = useState(false);
  const [coveredBy, setCoveredBy] = useState(null);
  const current = useRef("book");
  const modelViewer = useRef(null);
  const boxViewer = useRef(null);
  const runtime = useRef(null);
  const pushOrReplace = _temp;
  let showBook;
  let showBox;
  let showModel;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    showModel = nav_0 => {
      if (current.current === "model") {
        return;
      }
      const wasBox = current.current === "box";
      current.current = "model";
      setStage("model");
      setModelMounted(true);
      setViewing(true);
      modelViewer.current?.suspend(false);
      modelViewer.current?.enter();
      runtime.current?.suspend(true);
      if (wasBox) {
        retireBox();
      }
      pushOrReplace(nav_0, "model");
    };
    showBox = nav_1 => {
      if (current.current === "box") {
        return;
      }
      const wasModel = current.current === "model";
      current.current = "box";
      setStage("box");
      setBoxMounted(true);
      setBoxShown(true);
      runtime.current?.suspend(true);
      boxViewer.current?.suspend(false);
      if (wasModel) {
        modelViewer.current?.suspend(true);
        window.setTimeout(() => {
          if (current.current === "box") {
            setViewing(false);
          }
        }, HANDOFF_MS);
      }
      pushOrReplace(nav_1, "box");
    };
    showBook = fromHistory => {
      const was = current.current;
      if (was === "book") {
        return;
      }
      current.current = "book";
      setStage("book");
      runtime.current?.suspend(false);
      if (!fromHistory) {
        if ((window.history.state as {
          stagePushed?: boolean;
        })?.stagePushed) {
          window.history.back();
        } else {
          window.history.replaceState({
            bricks: true
          }, "", `${window.location.pathname}?view=build`);
        }
      }
      if (was === "model") {
        const v = modelViewer.current;
        if (!v) {
          return setViewing(false);
        }
        v.leave().then(gone => {
          if (gone && current.current === "book") {
            setViewing(false);
            v.suspend(true);
          }
        });
      } else {
        if (was === "box") {
          retireBox();
        }
      }
    };
    const retireBox = () => {
      boxViewer.current?.suspend(true);
      window.setTimeout(() => {
        if (current.current !== "box") {
          setBoxShown(false);
        }
      }, HANDOFF_MS);
    };
    t0 = () => {
      current.current = "book";
      setStage("book");
      setModelMounted(false);
      setBoxMounted(false);
      setBoxShown(false);
      setViewing(false);
      runtime.current?.suspend(false);
    };
    $[0] = showBook;
    $[1] = showBox;
    $[2] = showModel;
    $[3] = t0;
  } else {
    showBook = $[0];
    showBox = $[1];
    showModel = $[2];
    t0 = $[3];
  }
  const drop = t0;
  const t1 = stage !== "book" && coveredBy === stage || undefined;
  let t2;
  if ($[4] !== stage) {
    t2 = e => {
      if (e.animationName === "bricks-viewer-in") {
        setCoveredBy(stage);
      }
    };
    $[4] = stage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = {
      "data-covered": t1,
      onAnimationEnd: t2
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => current.current !== "book";
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = next => {
      modelViewer.current = next;
    };
    t6 = next_0 => {
      boxViewer.current = next_0;
    };
    t7 = next_1 => {
      runtime.current = next_1;
      next_1?.suspend(current.current !== "book");
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  let t8;
  if ($[13] !== boxMounted || $[14] !== boxShown || $[15] !== modelMounted || $[16] !== stage || $[17] !== t3 || $[18] !== viewing) {
    t8 = {
      stage,
      coverProps: t3,
      modelMounted,
      boxMounted,
      boxShown,
      viewing,
      isOpen: t4,
      showModel,
      showBox,
      showBook,
      drop,
      setModelViewer: t5,
      setBoxViewer: t6,
      setRuntime: t7
    };
    $[13] = boxMounted;
    $[14] = boxShown;
    $[15] = modelMounted;
    $[16] = stage;
    $[17] = t3;
    $[18] = viewing;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}

// Where the generator sits on the table: a segmented booklet / model / box
// switch once one's open. No text or chrome besides the three buttons —
// the icon and aria-pressed state carry which stage is showing.
function _temp(nav, kind) {
  if (nav === "none") {
    return;
  }
  const prev = window.history.state as {
    stagePushed?: boolean;
  } | null;
  const state = {
    ...prev,
    stageView: kind,
    stagePushed: nav === "push" || !!prev?.stagePushed
  };
  if (nav === "push") {
    window.history.pushState(state, "", urlFor(kind));
  } else {
    window.history.replaceState(state, "", urlFor(kind));
  }
}
export function StageSwitch(t0) {
  const $ = _c(20);
  const {
    stage,
    hasBox,
    onSelect
  } = t0;
  const t1 = stage === "book";
  let t2;
  if ($[0] !== onSelect) {
    t2 = () => onSelect("book");
    $[0] = onSelect;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <BookOpen size={18} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t2) {
    t4 = <button type="button" aria-label="Back to the booklet" aria-pressed={t1} aria-keyshortcuts="V" title="Back to the booklet (V)" onClick={t2}>{t3}</button>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = stage === "model";
  let t6;
  if ($[6] !== onSelect) {
    t6 = () => onSelect("model");
    $[6] = onSelect;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Rotate3d size={18} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t5 || $[10] !== t6) {
    t8 = <button type="button" aria-label="See the build in 3D" aria-pressed={t5} aria-keyshortcuts="V" title="See the build in 3D (V)" onClick={t6}>{t7}</button>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== hasBox || $[13] !== onSelect || $[14] !== stage) {
    t9 = hasBox && <button type="button" aria-label="See the box" aria-pressed={stage === "box"} aria-keyshortcuts="B" title="See the box (B)" onClick={() => onSelect("box")}><Package size={18} /></button>;
    $[12] = hasBox;
    $[13] = onSelect;
    $[14] = stage;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t8 || $[18] !== t9) {
    t10 = <div className="bricks-stage-switch" role="group" aria-label="View">{t4}{t8}{t9}</div>;
    $[16] = t4;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}